import { useCallback, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";

export const asList = (value) => (Array.isArray(value) ? value : []);
export const sameId = (left, right) =>
  left != null && right != null && String(left) === String(right);
export const asMarkets = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

// Match the current project's polling API, with quiet background requests and
// cancellation when the route changes. Wait for each request before scheduling
// another so a slow server cannot accumulate overlapping requests.
export function useMatchFeed(url, params, interval, enabled = true) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const query = JSON.stringify(params);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let timer;
    let pending = false;
    const load = async () => {
      if (pending || controller.signal.aborted) return;
      clearTimeout(timer);
      if (document.hidden) return;
      pending = true;
      try {
        const response = await apiGet(url, JSON.parse(query), {
          skipLoader: true,
          signal: controller.signal,
        });
        if (response.status !== 200 || !response.data?.success) {
          throw new Error(response.data?.message || "Unable to load data.");
        }
        if (!controller.signal.aborted) {
          setData(response.data.results ?? {});
          setError("");
        }
      } catch (failure) {
        if (!controller.signal.aborted) {
          setError(
            failure.response?.data?.message ||
              failure.message ||
              "Unable to load data.",
          );
        }
      } finally {
        pending = false;
        if (!controller.signal.aborted && interval) {
          timer = setTimeout(load, interval);
        }
      }
    };
    const onVisibility = () => {
      if (!document.hidden) load();
    };
    load();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      controller.abort();
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [url, query, interval, enabled, revision]);

  return {
    data,
    setData,
    error,
    refresh,
    loading: enabled && data === null && !error,
  };
}

export function groupEventBets(data) {
  const group = (rows, type) =>
    asList(rows).map((bet) => ({
      ...bet,
      betFaireType: bet.betFaireType || type,
    }));
  const groups = {
    betfair: group(data?.BetFairBet?.matched, "betfair"),
    bookmaker: group(data?.BookmakerBet, "bookmaker"),
    fancy: group(data?.FancyBet, "fancy"),
    sportBook: group(data?.SportBooksBet, "sportBook"),
  };
  groups.all = Object.values(groups)
    .flat()
    .sort(
      (a, b) =>
        new Date(b.createdAt || b.timeInserted || 0) -
        new Date(a.createdAt || a.timeInserted || 0),
    );
  return groups;
}

export default function useSportAnalytics(eventId, marketId, userId) {
  const detailFeed = useMatchFeed(apiPath.matchDetail, { eventId }, 30000);
  const detail = detailFeed.data;
  const markets = useMemo(() => {
    if (!detail) return [];
    const list = asList(detail.marketList);
    const selected = list.find((market) => sameId(market.marketId, marketId));
    const primary = {
      marketId,
      market: selected?.market || "Match Odds",
      jsonData: selected?.jsonData || detail.jsonData || [],
    };
    const additional =
      detail.gameType === "soccer"
        ? list.filter(
            (market) =>
              !sameId(market.marketId, marketId) &&
              asList(market.jsonData).length,
          )
        : [];
    return [primary, ...additional];
  }, [detail, marketId]);
  const marketIds = markets.map((market) => market.marketId).join(",");
  const oddsFeed = useMatchFeed(
    apiPath.getMatchOdds,
    {
      marketId: marketIds,
      ...(markets.length > 1 ? { multi: true, eventId } : {}),
    },
    2000,
    Boolean(detail && marketIds && detail.matchOdds === "on"),
  );
  const bookmakerFeed = useMatchFeed(
    apiPath.bookmakerOdds,
    { eventId },
    2000,
    Boolean(
      (detail?.gameType === "cricket" || String(detail?.eventType) === "4") &&
      detail?.hasBookmaker &&
      detail?.bookMaker === "on",
    ),
  );
  const fancyFeed = useMatchFeed(
    apiPath.getFancyOdds,
    { eventId },
    5000,
    Boolean(
      detail && String(detail.eventType) === "4" && detail.fancy === "on",
    ),
  );
  const premiumEnabled = Boolean(
    detail?.premiumFancy == "on" || detail?.hasSportBook
  );
  const premiumFeed = useMatchFeed(
    apiPath.getPremiumFancy,
    { eventId },
    2000,
    premiumEnabled,
  );
  const betsFeed = useMatchFeed(apiPath.eventsBets, { eventId }, 5000);
  const positionsFeed = useMatchFeed(
    apiPath.getMatchDetailPosition,
    { eventId },
    5000,
    Boolean(detail),
  );
  const [premiumExpansion, setPremiumExpansion] = useState({});
  const premium = useMemo(() => {
    if (!premiumEnabled) return [];
    return asList(premiumFeed.data?.data ?? premiumFeed.data)
      .filter(
        (market) => market.eventId == null || sameId(market.eventId, eventId),
      )
      .map((market) => {
        const id = market.id ?? market.marketId ?? market.marketName;
        // Match the old SportAnalytics position mapping: market name first,
        // then sportsBookSelection.id against fancySelectionId.
        const marketPositions = asList(
          positionsFeed.data?.fancyAndPremium,
        ).filter((position) => position.fancyName === market.marketName);
        return {
          ...market,
          id,
          expand: premiumExpansion[id] ?? market.expand ?? true,
          sportsBookSelection: asList(market.sportsBookSelection).map(
            (selection) => ({
              ...selection,
              position: marketPositions.length
                ? marketPositions.find((position) =>
                    sameId(position.fancySelectionId, selection.id),
                  )?.position
                : selection.position,
            }),
          ),
        };
      });
  }, [
    premiumEnabled,
    premiumFeed.data,
    positionsFeed.data,
    premiumExpansion,
    eventId,
  ]);
  console.log(premium,'premium')
  const togglePremium = useCallback(
    (id) => {
      const market = premium.find((item) => sameId(item.id, id));
      if (!market) return;
      setPremiumExpansion((previous) => ({
        ...previous,
        [id]: !(previous[id] ?? market.expand),
      }));
    },
    [premium],
  );
  const [toss, setToss] = useState(null);
  const [socketError, setSocketError] = useState("");
  const socketUrl = detail?.socketUrl || process.env.REACT_APP_SOCKET;
  const socketEventId = detail?.eventId;
  const { setData: setOdds } = oddsFeed;
  const { setData: setBookmaker } = bookmakerFeed;
  const { setData: setFancy } = fancyFeed;

  useEffect(() => {
    if (!socketUrl || !socketEventId) return;
    const socket = io(socketUrl, {
      query: {
        token: userId || 112233,
        userType: "front",
        eventId: socketEventId,
      },
      transports: ["websocket"],
      forceNew: true,
    });
    const forEvent = (market) => sameId(market?.eventId, eventId);
    socket.on("listenBetFairOdds", (message) => {
      const updates = asMarkets(message?.results).filter(forEvent);
      if (!updates.length) return;
      setOdds((previous) => {
        const next = [...asMarkets(previous)];
        updates.forEach((market) => {
          const index = next.findIndex((item) => sameId(item.mi, market.mi));
          if (index >= 0) next[index] = market;
          else next.push(market);
        });
        return next;
      });
    });
    socket.on("listenBookmakerOdds", (message) => {
      if (forEvent(message)) setBookmaker(message.results ?? {});
    });
    socket.on("listenDiamondFancy", (message) => {
      if (forEvent(message)) setFancy(asList(message.results));
    });
    socket.on("listenBetFairTossOdds", (message) => {
      const market = asMarkets(message?.results).find(forEvent);
      if (market) setToss(market);
    });
    socket.on("connect", () => setSocketError(""));
    socket.on("disconnect", () => {
      setToss(null);
      setSocketError(
        "Live connection interrupted. Odds and bets continue to refresh.",
      );
    });
    socket.on("connect_error", () => {
      setToss(null);
      setSocketError(
        "Live connection unavailable. Odds and bets continue to refresh.",
      );
    });
    const reconnect = () => {
      if (!document.hidden && !socket.connected) socket.connect();
    };
    document.addEventListener("visibilitychange", reconnect);
    return () => {
      document.removeEventListener("visibilitychange", reconnect);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [
    socketUrl,
    socketEventId,
    eventId,
    userId,
    setOdds,
    setBookmaker,
    setFancy,
  ]);

  const refresh = () => {
    [
      detailFeed,
      oddsFeed,
      bookmakerFeed,
      fancyFeed,
      premiumFeed,
      betsFeed,
      positionsFeed,
    ].forEach((feed) => feed.refresh());
  };
  const groups = useMemo(() => groupEventBets(betsFeed.data), [betsFeed.data]);
  const feedErrors = [
    ["Match details", detailFeed.error],
    ["Match odds", oddsFeed.error],
    ["Bookmaker", bookmakerFeed.error],
    ["Fancy", fancyFeed.error],
    ["Premium odds", premiumEnabled ? premiumFeed.error : ""],
    ["Bets", betsFeed.error],
    ["Positions", positionsFeed.error],
  ]
    .filter(([, error]) => error)
    .map(([label, error]) => `${label}: ${error}`);

  return {
    detail,
    markets,
    odds: asMarkets(oddsFeed.data),
    bookmakerOdds: asMarkets(bookmakerFeed.data),
    fancy: asList(fancyFeed.data),
    premium,
    togglePremium,
    groups,
    positions: positionsFeed.data || {},
    toss,
    refresh,
    loading: detailFeed.loading,
    errors: [...feedErrors, ...(socketError ? [socketError] : [])],
  };
}
